/**
 * BulkEnrichModal - Confirmation modal for bulk phone enrichment.
 *
 * Groups contacts by eligibility and only enriches qualifying contacts.
 * Uses the unified usePhoneEnrichment hook for eligibility data.
 */

import { CheckCircle, XCircle } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { formatCredits } from '@/lib/billing/creditCost';
import { EntityBadgeByIds } from '@/lib/crm/components/badges';
import { submitEventsOptimistic } from '@/lib/crm/store/submitEventsOptimistic';
import { logger } from '@/lib/logger';
import { type CrmObject, getPropertyValue } from '@/types/crm';

import { usePhoneEnrichment } from './usePhoneEnrichment';

export interface BulkEnrichModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Organization ID */
  orgId: number;
  /** Contacts to potentially enrich */
  contacts: CrmObject[];
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback after successful enrichment */
  onSuccess?: () => void;
}

/** Batch size for event submission (max 1000 events per request, 2 events per contact) */
const BATCH_SIZE = 250;

/**
 * BulkEnrichModal displays a confirmation dialog for bulk phone enrichment.
 *
 * Groups contacts into:
 * - Will enrich: Has LinkedIn URL, no existing phone enrichment
 * - Missing LinkedIn: Cannot enrich without LinkedIn URL
 * - Already enriched: Has existing phone enrichment request
 */
export function BulkEnrichModal({
  isOpen,
  orgId,
  contacts,
  onClose,
  onSuccess,
}: BulkEnrichModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuthInfo();

  // Use unified hook for enrichment eligibility data
  // Only enabled when modal is open to avoid unnecessary fetches
  const {
    infoMap,
    isLoading: isLoadingEnrichments,
    isError: isErrorEnrichments,
    creditBalanceEnabled,
    creditCostPerContact,
    availableCredits,
  } = usePhoneEnrichment(isOpen ? orgId : undefined, isOpen ? contacts : []);

  // Group contacts by eligibility (priority order) using data from the unified hook
  const {
    willEnrich,
    alreadyHasPhone,
    enrichmentInProgress,
    missingLinkedIn,
    alreadyAttempted,
  } = useMemo(() => {
    const willEnrich: CrmObject[] = [];
    const alreadyHasPhone: CrmObject[] = [];
    const enrichmentInProgress: CrmObject[] = [];
    const missingLinkedIn: CrmObject[] = [];
    const alreadyAttempted: CrmObject[] = [];

    for (const contact of contacts) {
      const info = infoMap.get(contact.entity_id);

      // Use eligibility info from hook, with safe fallbacks
      const hasPhoneNumber =
        info?.hasPhoneNumber ??
        !!getPropertyValue(contact.properties?.ahoy_phone_number);
      const hasLinkedIn =
        info?.hasLinkedIn ??
        !!getPropertyValue(contact.properties?.ahoy_linkedin_url);
      const hasPhoneEnrichment = info?.hasPhoneEnrichment ?? false;
      const isPending = info?.status === 'pending';

      // Priority order matches usePhoneEnrichment eligibility logic
      if (hasPhoneNumber) {
        alreadyHasPhone.push(contact);
      } else if (isPending) {
        enrichmentInProgress.push(contact);
      } else if (!hasLinkedIn) {
        missingLinkedIn.push(contact);
      } else if (hasPhoneEnrichment) {
        alreadyAttempted.push(contact);
      } else {
        willEnrich.push(contact);
      }
    }

    return {
      willEnrich,
      alreadyHasPhone,
      enrichmentInProgress,
      missingLinkedIn,
      alreadyAttempted,
    };
  }, [contacts, infoMap]);

  const enrichGroupEntityIds = useMemo(
    () => ({
      willEnrich: willEnrich.map(o => o.entity_id),
      alreadyHasPhone: alreadyHasPhone.map(o => o.entity_id),
      enrichmentInProgress: enrichmentInProgress.map(o => o.entity_id),
      missingLinkedIn: missingLinkedIn.map(o => o.entity_id),
      alreadyAttempted: alreadyAttempted.map(o => o.entity_id),
    }),
    [
      willEnrich,
      alreadyHasPhone,
      enrichmentInProgress,
      missingLinkedIn,
      alreadyAttempted,
    ]
  );

  const estimatedCost = useMemo(
    () => willEnrich.length * creditCostPerContact,
    [willEnrich, creditCostPerContact]
  );

  const willExceed = useMemo(
    () => availableCredits !== undefined && estimatedCost > availableCredits,
    [availableCredits, estimatedCost]
  );

  const handleConfirm = useCallback(async () => {
    if (willEnrich.length === 0) {
      return;
    }

    setIsSubmitting(true);
    let successCount = 0;

    try {
      // Process contacts in batches (max 250 contacts = 500 events per request)
      // Track progress to avoid re-submitting successful batches on retry
      for (let i = 0; i < willEnrich.length; i += BATCH_SIZE) {
        const batch = willEnrich.slice(i, i + BATCH_SIZE);
        const ids = batch.map(() => crypto.randomUUID());

        await submitEventsOptimistic({
          orgId,
          userId: user?.id ?? 0,
          entityTypeName: 'ahoy_enrichment_request',
          primaryEntityId: ids[0],
          errorToast: false,
          build: b => {
            batch.forEach((contact, idx) => {
              b.objectCreated(ids[idx], 'ahoy_enrichment_request', {
                ahoy_enrichment_type: 'phone',
              }).associationAdded(
                ids[idx],
                'ahoy_enrichment_request',
                'ahoy_enrichment_request_to_contact',
                contact.entity_id
              );
            });
          },
        });
        successCount += batch.length;
      }

      toast.success('Phone enrichment started', {
        description: `${willEnrich.length} contact${willEnrich.length === 1 ? '' : 's'} queued for enrichment`,
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      logger.error(
        'Bulk phone enrichment request failed',
        error instanceof Error ? error : undefined,
        { component: 'crm_enrichment' }
      );
      if (successCount > 0) {
        // Partial success - some batches were submitted before failure
        toast.warning('Partial enrichment', {
          description: `${successCount} contact${successCount === 1 ? '' : 's'} queued, but ${willEnrich.length - successCount} failed. Please try again for the remaining contacts.`,
        });
        onSuccess?.();
        onClose();
      } else {
        toast.error('Enrichment failed', {
          description:
            "We couldn't queue these contacts for enrichment. Try again.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [willEnrich, orgId, user, onSuccess, onClose]);

  const isLoading = isLoadingEnrichments;
  const isError = isErrorEnrichments;
  const totalSelected = contacts.length;
  const skippedCount =
    alreadyHasPhone.length +
    enrichmentInProgress.length +
    missingLinkedIn.length +
    alreadyAttempted.length;

  let eligibilityContent: React.ReactNode;
  if (isLoading) {
    eligibilityContent = (
      <div className='flex items-center gap-2 text-sm text-fg-secondary'>
        <LoadingIndicator type='line-simple' size='sm' />
        Checking eligibility...
      </div>
    );
  } else if (isError) {
    eligibilityContent = (
      <div className='flex items-center gap-2 text-sm text-error-500'>
        <XCircle className='w-4 h-4' />
        We couldn't check eligibility. Try again.
      </div>
    );
  } else {
    eligibilityContent = (
      <>
        {/* Will enrich */}
        <div className='flex items-center gap-2 text-sm'>
          <CheckCircle className='w-4 h-4 text-success-500' />
          <span className='text-fg-primary font-medium'>
            {willEnrich.length}
          </span>
          <span className='text-fg-secondary'>
            contact{willEnrich.length === 1 ? '' : 's'} will be enriched
          </span>
        </div>
        {willEnrich.length > 0 && (
          <div className='ml-6'>
            <EntityBadgeByIds
              entityIds={enrichGroupEntityIds.willEnrich}
              orgId={orgId}
              maxVisible={5}
              variant='xs'
            />
          </div>
        )}

        {/* Credit balance info */}
        {creditBalanceEnabled && willEnrich.length > 0 && (
          <div className='text-sm text-fg-secondary'>
            <span>
              This will use {formatCredits(estimatedCost)} (
              {creditCostPerContact} per contact).
            </span>
            {availableCredits !== undefined && (
              <span>
                {' '}
                You have {formatCredits(availableCredits)} remaining.
              </span>
            )}
            {willExceed && (
              <div className='text-[var(--color-text-warning-primary)]'>
                This will exceed your credit balance.
              </div>
            )}
          </div>
        )}

        {/* Skipped groups (in priority order) */}
        {alreadyHasPhone.length > 0 && (
          <>
            <div className='flex items-center gap-2 text-sm'>
              <XCircle className='w-4 h-4 text-fg-quaternary' />
              <span className='text-fg-tertiary'>{alreadyHasPhone.length}</span>
              <span className='text-fg-quaternary'>
                skipped – already has phone number
              </span>
            </div>
            <div className='ml-6'>
              <EntityBadgeByIds
                entityIds={enrichGroupEntityIds.alreadyHasPhone}
                orgId={orgId}
                maxVisible={5}
                variant='xs'
              />
            </div>
          </>
        )}

        {enrichmentInProgress.length > 0 && (
          <>
            <div className='flex items-center gap-2 text-sm'>
              <XCircle className='w-4 h-4 text-fg-quaternary' />
              <span className='text-fg-tertiary'>
                {enrichmentInProgress.length}
              </span>
              <span className='text-fg-quaternary'>
                skipped – enrichment in progress
              </span>
            </div>
            <div className='ml-6'>
              <EntityBadgeByIds
                entityIds={enrichGroupEntityIds.enrichmentInProgress}
                orgId={orgId}
                maxVisible={5}
                variant='xs'
              />
            </div>
          </>
        )}

        {missingLinkedIn.length > 0 && (
          <>
            <div className='flex items-center gap-2 text-sm'>
              <XCircle className='w-4 h-4 text-fg-quaternary' />
              <span className='text-fg-tertiary'>{missingLinkedIn.length}</span>
              <span className='text-fg-quaternary'>
                skipped – missing LinkedIn URL
              </span>
            </div>
            <div className='ml-6'>
              <EntityBadgeByIds
                entityIds={enrichGroupEntityIds.missingLinkedIn}
                orgId={orgId}
                maxVisible={5}
                variant='xs'
              />
            </div>
          </>
        )}

        {alreadyAttempted.length > 0 && (
          <>
            <div className='flex items-center gap-2 text-sm'>
              <XCircle className='w-4 h-4 text-fg-quaternary' />
              <span className='text-fg-tertiary'>
                {alreadyAttempted.length}
              </span>
              <span className='text-fg-quaternary'>
                skipped – already attempted phone enrichment
              </span>
            </div>
            <div className='ml-6'>
              <EntityBadgeByIds
                entityIds={enrichGroupEntityIds.alreadyAttempted}
                orgId={orgId}
                maxVisible={5}
                variant='xs'
              />
            </div>
          </>
        )}

        {/* Summary when some skipped */}
        {skippedCount > 0 && willEnrich.length > 0 && (
          <p className='text-sm text-fg-tertiary pt-2 border-t border-border-secondary'>
            {willEnrich.length} of {totalSelected} selected contacts will be
            enriched.
          </p>
        )}

        {/* No contacts eligible */}
        {willEnrich.length === 0 && (
          <p className='text-sm text-fg-secondary pt-2'>
            None of the selected contacts are eligible for phone enrichment.
          </p>
        )}
      </>
    );
  }

  let confirmButtonLabel: string;
  if (isError) {
    confirmButtonLabel = 'Unable to enrich';
  } else if (willEnrich.length === 0) {
    confirmButtonLabel = 'No contacts to enrich';
  } else {
    const plural = willEnrich.length === 1 ? '' : 's';
    confirmButtonLabel = `Enrich ${willEnrich.length} contact${plural}`;
  }

  const footer = (
    <div className='flex items-center justify-end gap-3'>
      <Button
        color='secondary'
        size='md'
        onClick={onClose}
        isDisabled={isSubmitting}
      >
        Cancel
      </Button>
      <Button
        color='primary'
        size='md'
        onClick={handleConfirm}
        isDisabled={
          isSubmitting || isLoading || isError || willEnrich.length === 0
        }
        isLoading={isSubmitting}
      >
        {confirmButtonLabel}
      </Button>
    </div>
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isSubmitting) {
        onClose();
      }
    },
    [isSubmitting, onClose]
  );

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable={!isSubmitting}
    >
      <Modal className='sm:max-w-lg'>
        <Dialog
          title='Enrich phone numbers'
          isCloseDisabled={isSubmitting}
          footer={footer}
        >
          <div className='space-y-3'>{eligibilityContent}</div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
